import { useCallback, useEffect, useRef } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { useSlsHighlightsContext } from '@fs/zion-sls'
import { removeIndex } from '../../shared/utils/utils'
import { useR2Highlights } from '../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../shared/providers/overlayContextProvider/OverlayContext'
import { getHighlightSubjectPersonId } from '../utils/highlightSubject'

const R2 = 'index'
const FULLTEXT = 'fullText'
const FULLNAME = 'Full Name Review'
const FAMILYREVIEW = 'Family Review'

const appNames = [{ name: R2 }, { name: FULLTEXT }, { name: FULLNAME }, { name: FAMILYREVIEW }]

// The entire purpose of this hook is to provide the event listeners for the highlights
// The event listeners are the same for both splitScreen and non-splitScreen, they just use different state and api
const useSlsHighlightEventListeners = ({ forSplitScreen, state, dispatch }) => {
  const overlayContext = useOverlayContext()
  const { query } = useQueryParams()
  const app = useRef(appNames.find((appName) => query.view === appName.name || query.task === appName.name)?.name)
  const {
    // Do not use these directly, use the state, and api found below
    displayHighlights,
    displayHighlightsApi,
    splitScreenDisplayHighlights,
    splitScreenDisplayHighlightsApi,
  } = useR2Highlights()
  const { activeFieldInfo } = useSlsHighlightsContext()

  // This is just to simplify the code below
  // The logic for the listeners is the same for splitScreen and non-splitScreen, but there are two sets of state and api
  // So we use a ref to return the correct one
  const highlightsState = useRef(forSplitScreen ? splitScreenDisplayHighlights : displayHighlights)
  const api = useRef(forSplitScreen ? splitScreenDisplayHighlightsApi : displayHighlightsApi)
  useEffect(() => {
    highlightsState.current = forSplitScreen ? splitScreenDisplayHighlights : displayHighlights
  }, [displayHighlights, splitScreenDisplayHighlights, forSplitScreen])
  useEffect(() => {
    api.current = forSplitScreen ? splitScreenDisplayHighlightsApi : displayHighlightsApi
  }, [displayHighlightsApi, splitScreenDisplayHighlightsApi, forSplitScreen])

  /**
   * Handle hover highlighting for a specific highlight.
   * @param {string} id - The ID of the highlight.
   * @param {boolean} shouldHover - Indicates whether to hover or unhover the highlight.
   */
  const handleFullTextHover = useCallback(
    (id, highlight) => {
      api.current.hover(id)
      dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: highlight.fieldId })
    },
    [dispatch]
  )

  const hasMatchingPersonId = useCallback((personIds, otherPersonIds) => {
    return personIds.some((myPids) => otherPersonIds.some((yourPids) => yourPids === myPids))
  }, [])

  const handlePersonsListHover = useCallback(
    (highlight) => {
      const personIds = highlight.personIds
      if (!personIds[0]) return

      const otherHighlightsRelatedToPerson = highlightsState.current.ids.filter((hl) =>
        hasMatchingPersonId(highlightsState.current.highlights[hl].personIds, personIds)
      )
      api.current.hover(otherHighlightsRelatedToPerson)
      dispatch({ type: 'SET_HIGHLIGHTED_PERSON_ID', value: personIds[0] })
    },
    [dispatch, hasMatchingPersonId]
  )

  const handleR2Hover = useCallback(
    (id, highlight) => {
      if (overlayContext.topMostPanel && !overlayContext.topMostPanel()) {
        handlePersonsListHover(highlight)
      } else {
        api.current.hover(id)
        // Always set the highlighted person ID when hovering over image highlights
        const personIds = highlight.personIds
        if (personIds && personIds.length > 0 && personIds[0]) {
          dispatch({ type: 'SET_HIGHLIGHTED_PERSON_ID', value: personIds[0] })
        }
      }
    },
    [overlayContext, handlePersonsListHover, dispatch]
  )

  const handleUnhover = useCallback(() => {
    api.current.hover()
    if (app.current === FULLTEXT) {
      dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: '' })
    } else if (app.current === R2) {
      // Always clear the highlighted person ID when unhovering
      dispatch({ type: 'SET_HIGHLIGHTED_PERSON_ID', value: '' })
    }
  }, [dispatch])

  const onHoverHighlight = useCallback(
    (id, shouldHover) => {
      if (shouldHover) {
        const highlight = highlightsState.current.highlights[id]
        if (app.current === FULLTEXT) {
          handleFullTextHover(id, highlight)
        } else if (app.current === R2) {
          handleR2Hover(id, highlight)
        }
      } else {
        handleUnhover()
      }
    },
    [handleFullTextHover, handleR2Hover, handleUnhover]
  )
  /**
   * Handling hovering on record level highlights
   * @param {String} id - the id of the record related to that highlight
   * @param {Boolean} shouldHover whether or not the highlight is being hovered
   */
  const onHoverRecordHighlight = useCallback(
    (id, shouldHover) => {
      if (app.current === R2) dispatch({ type: 'SET_STATE', value: { highlightedRecordId: shouldHover ? id : '' } })
    },
    [dispatch]
  )
  /**
   * Handle focus highlighting for a specific highlight.
   * @param {string} id - The ID of the highlight.
   */
  const onFocusHighlight = useCallback(
    (id) => {
      if (app.current === R2 && overlayContext.topMostPanel && !overlayContext.topMostPanel()) {
        // We're on the persons list panel, so selecting should take us to the PersonOverview panel with that person's data visible
        const highlight = highlightsState.current.highlights[id]
        if (highlight?.personIds[0]) {
          dispatch({ type: 'SET_SELECTED_PERSON_ID', value: highlight.personIds[0] })
        }
      } else {
        if (state.creatingRect) {
          dispatch({
            type: 'SET_CREATING_RECT',
            value: false,
          })
        }
        api.current.select(id)
      }
    },
    [dispatch, overlayContext, state.creatingRect]
  )

  /**
   * Handle updating the position and or size for a specific highlight.
   * @param {string} id - The ID of the highlight.
   * @param {Object} update - The updated position object.
   */
  const onUpdateHighlight = useCallback((id, update) => {
    const { x, y, width, height } = highlightsState.current.highlights[id].position
    if (
      update.x.toFixed(4) === x.toFixed(4) &&
      update.y.toFixed(4) === y.toFixed(4) &&
      update.width.toFixed(4) === width.toFixed(4) &&
      update.height.toFixed(4) === height.toFixed(4)
    ) {
      return
    }
    // It should be noted that this update is being made only to the displayHighlights state, not the ui state
    api.current.update(id, { position: update, change: 'edit' })
  }, [])

  const onToggleAttachedListener = useCallback(
    (id) => {
      const highlight = highlightsState.current.highlights[id]
      const subjectPersonId = getHighlightSubjectPersonId(state, activeFieldInfo)
      if (highlight.attached) {
        const redetached = highlight.originalHighlight.fieldId !== activeFieldInfo?.id // Whether highlight was attached to active field before being detached again
        api.current.update(id, {
          attached: false,
          change: redetached ? false : 'deselected',
          fieldType: '',
          fieldId: redetached ? highlight.originalHighlight.fieldId : '',
          personIds: highlight.personIds.length
            ? removeIndex(highlight.personIds, highlight.personIds.indexOf(subjectPersonId))
            : highlight.personIds, // Somehow we get an empty personIds array on some projects which throws an error without this.
        })
      } else {
        api.current.update(id, {
          attached: true,
          change: 'edit',
          fieldType: activeFieldInfo?.fieldType,
          fieldId: activeFieldInfo?.id,
          personIds: [...highlight.personIds, subjectPersonId],
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedPersonId, state.createdPersonId, activeFieldInfo]
  )
  return { onHoverHighlight, onFocusHighlight, onUpdateHighlight, onToggleAttachedListener, onHoverRecordHighlight }
}

export default useSlsHighlightEventListeners
